/**
 * Template Registry — maps template IDs to layout components + metadata.
 *
 * Each template is a LAYOUT BLUEPRINT that defines WHERE elements are positioned.
 * All templates render the same slide data — switching templates never loses data.
 *
 * Template = per-slide (stored as `slide.template`). Falls back to `carousel.template`
 * for legacy data, then to DEFAULT_TEMPLATE.
 */
import { ForestClassicLayout } from './ForestClassicLayout';
import { BoldMagazineLayout } from './BoldMagazineLayout';
import { CleanMinimalLayout } from './CleanMinimalLayout';
import { TopHeavyLayout } from './TopHeavyLayout';
import { NoirEleganceLayout } from './NoirEleganceLayout';
import { BrutalistLayout } from './BrutalistLayout';
import { SplitLayout } from './SplitLayout';
import { FullBleedLayout } from './FullBleedLayout';
import { GradientCardLayout } from './GradientCardLayout';
import { SidebarAccentLayout } from './SidebarAccentLayout';
import { CenteredStackLayout } from './CenteredStackLayout';
import { DynamicDiagonalLayout } from './DynamicDiagonalLayout';
import { CornerFrameLayout } from './CornerFrameLayout';
import { RibbonBandLayout } from './RibbonBandLayout';
import { NormiesFullBgLayout } from './NormiesFullBgLayout';
import { NormiesPolaroidLayout } from './NormiesPolaroidLayout';
import { NormiesBeforeAfterLayout } from './NormiesBeforeAfterLayout';
import { NormiesDashboardLayout } from './NormiesDashboardLayout';
import { NormiesBurnLayout } from './NormiesBurnLayout';
import { NormiesGmLayout } from './NormiesGmLayout';
import { NormiesExplainerLayout } from './NormiesExplainerLayout';

/**
 * Template categories for the Browse zone in the template catalog.
 * Each category has an id and display label.
 */
export const TEMPLATE_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'editorial', label: 'Editorial' },
  { id: 'minimal', label: 'Minimal' },
  { id: 'bold', label: 'Bold' },
  { id: 'luxury', label: 'Luxury' },
  { id: 'community', label: 'Community' },
  { id: 'tech', label: 'Tech' },
  { id: 'normies', label: 'Normies' },
];

export const TEMPLATES = {
  'forest-classic': {
    id: 'forest-classic',
    name: 'Forest Classic',
    description: 'Glass panel at bottom, logo at top',
    component: ForestClassicLayout,
    defaultFontPreset: 0, // Classic / Inter
    categories: ['community'],
    isPreset: true,
    accent: '#66ccff',
    accentGlow: 'rgba(102, 204, 255, 0.25)',
    // uses DEFAULT_ELEMENTS opacities (muted: 0.70, subtle: 0.55)
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-1200, 0] } },
      logo: { axis: 'both', range: { x: [-40, 900], y: [-40, 500] } },
    },
  },
  'bold-magazine': {
    id: 'bold-magazine',
    name: 'Bold Magazine',
    description: 'Centered editorial with divider',
    component: BoldMagazineLayout,
    defaultFontPreset: 3, // Editorial / Playfair Display
    categories: ['editorial', 'bold'],
    isPreset: true,
    accent: '#ff3399',
    accentGlow: 'rgba(255, 51, 153, 0.25)',
    contrast: { footer: 0.50 },
    // uses DEFAULT_ELEMENTS opacities (renders own title, delegates rest to SlideContent)
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-500, 500] } },
    },
  },
  'clean-minimal': {
    id: 'clean-minimal',
    name: 'Clean Minimal',
    description: 'Left-aligned, airy spacing',
    component: CleanMinimalLayout,
    defaultFontPreset: 4, // Minimal / DM Sans
    categories: ['minimal'],
    isPreset: true,
    accent: '#ffaa00',
    accentGlow: 'rgba(255, 170, 0, 0.25)',
    contrast: { muted: 0.60, subtle: 0.45, footer: 0.40 },
    nudgeConstraints: {
      content: { axis: 'both', range: { x: [-200, 200], y: [-400, 400] } },
      logo: { axis: 'both', range: { x: [-40, 300], y: [-40, 300] } },
    },
  },
  'top-heavy': {
    id: 'top-heavy',
    name: 'Top Heavy',
    description: 'Glass panel at top, logo at bottom',
    component: TopHeavyLayout,
    defaultFontPreset: 2, // Modern / Montserrat
    categories: ['bold', 'community'],
    isPreset: false,
    accent: '#34d399',
    accentGlow: 'rgba(52, 211, 153, 0.25)',
    contrast: { footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [0, 800] } },
      logo: { axis: 'both', range: { x: [-40, 600], y: [-300, 100] } },
    },
  },
  'noir-elegance': {
    id: 'noir-elegance',
    name: 'Noir Elegance',
    description: 'Luxury centered with ornamental lines',
    component: NoirEleganceLayout,
    defaultFontPreset: 5, // Luxe / Cormorant Garamond
    categories: ['luxury'],
    isPreset: true,
    accent: '#d4af37',
    accentGlow: 'rgba(212, 175, 55, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.35 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-500, 500] } },
    },
  },
  'brutalist': {
    id: 'brutalist',
    name: 'Brutalist',
    description: 'Raw monospace, hard grid',
    component: BrutalistLayout,
    defaultFontPreset: 6, // Brutalist / Space Mono
    categories: ['bold'],
    isPreset: false,
    isCustom: true,
    accent: '#ff4444',
    accentGlow: 'rgba(255, 68, 68, 0.25)',
    contrast: { muted: 0.50, subtle: 0.35, footer: 0.30 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-500, 500] } },
    },
  },
  'split': {
    id: 'split',
    name: 'Split',
    description: 'Left panel, right background',
    component: SplitLayout,
    defaultFontPreset: 7, // Friendly / Poppins
    categories: ['minimal'],
    isPreset: false,
    isCustom: true,
    accent: '#00cccc',
    accentGlow: 'rgba(0, 204, 204, 0.25)',
    contrast: { muted: 0.65, subtle: 0.45, footer: 0.40 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-400, 400] } },
      logo: { axis: 'both', range: { x: [-40, 250], y: [-40, 300] } },
    },
  },
  'full-bleed': {
    id: 'full-bleed',
    name: 'Full Bleed',
    description: 'Giant poster title, minimal body',
    component: FullBleedLayout,
    defaultFontPreset: 8, // Impact / Bebas Neue
    categories: ['bold'],
    isPreset: false,
    isCustom: true,
    accent: '#cc33ff',
    accentGlow: 'rgba(204, 51, 255, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, body: 0.60 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-500, 500] } },
    },
  },
  'gradient-card': {
    id: 'gradient-card',
    name: 'Gradient Card',
    description: 'Floating card with gradient glow border',
    component: GradientCardLayout,
    defaultFontPreset: 2, // Modern / Montserrat
    categories: ['tech', 'minimal'],
    isPreset: false,
    accent: '#7766ff',
    accentGlow: 'rgba(119, 102, 255, 0.25)',
    contrast: { subtle: 0.50, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'both', range: { x: [-200, 200], y: [-400, 400] } },
    },
  },
  'sidebar-accent': {
    id: 'sidebar-accent',
    name: 'Sidebar Accent',
    description: 'Swiss-style accent bar, clean layout',
    component: SidebarAccentLayout,
    defaultFontPreset: 4, // Minimal / DM Sans
    categories: ['editorial', 'minimal'],
    isPreset: false,
    accent: '#00cc88',
    accentGlow: 'rgba(0, 204, 136, 0.25)',
    contrast: { muted: 0.65, subtle: 0.45, footer: 0.40 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-400, 400] } },
      logo: { axis: 'both', range: { x: [-40, 300], y: [-40, 300] } },
    },
  },
  'centered-stack': {
    id: 'centered-stack',
    name: 'Centered Stack',
    description: 'Zen centered layout, generous spacing',
    component: CenteredStackLayout,
    defaultFontPreset: 1, // Elegant / Raleway
    categories: ['minimal', 'luxury'],
    isPreset: false,
    accent: '#aa88ff',
    accentGlow: 'rgba(170, 136, 255, 0.25)',
    contrast: { muted: 0.65, subtle: 0.45, footer: 0.40 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-500, 500] } },
      logo: { axis: 'both', range: { x: [-200, 200], y: [-40, 400] } },
    },
  },
  'dynamic-diagonal': {
    id: 'dynamic-diagonal',
    name: 'Dynamic Diagonal',
    description: 'Bold diagonal band, high energy',
    component: DynamicDiagonalLayout,
    defaultFontPreset: 8, // Impact / Bebas Neue
    categories: ['bold', 'tech'],
    isPreset: false,
    accent: '#88ff00',
    accentGlow: 'rgba(136, 255, 0, 0.25)',
    contrast: { subtle: 0.50, footer: 0.45 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-400, 400] } },
      logo: { axis: 'both', range: { x: [-40, 500], y: [-100, 400] } },
    },
  },
  'corner-frame': {
    id: 'corner-frame',
    name: 'Corner Frame',
    description: 'Asymmetric corner accents, editorial',
    component: CornerFrameLayout,
    defaultFontPreset: 5, // Luxe / Cormorant Garamond
    categories: ['editorial', 'luxury'],
    isPreset: false,
    accent: '#ff6688',
    accentGlow: 'rgba(255, 102, 136, 0.25)',
    contrast: { muted: 0.65, subtle: 0.40, footer: 0.35 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-800, 0] } },
      logo: { axis: 'both', range: { x: [-40, 600], y: [-40, 500] } },
    },
  },
  'ribbon-band': {
    id: 'ribbon-band',
    name: 'Ribbon Band',
    description: 'Horizontal content strip over background',
    component: RibbonBandLayout,
    defaultFontPreset: 7, // Friendly / Poppins
    categories: ['bold', 'community'],
    isPreset: false,
    accent: '#ff8844',
    accentGlow: 'rgba(255, 136, 68, 0.25)',
    contrast: { subtle: 0.45, footer: 0.45 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-400, 400] } },
      logo: { axis: 'both', range: { x: [-400, 40], y: [-40, 300] } },
    },
  },
  'normies-full-bg': {
    id: 'normies-full-bg',
    name: 'Normies Full BG',
    description: 'Full-bleed Normie background with text overlay',
    component: NormiesFullBgLayout,
    defaultFontPreset: 6, // Tech / IBM Plex Mono
    categories: ['normies'],
    isPreset: true,
    accent: '#48494b',
    accentGlow: 'rgba(72, 73, 75, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-300, 300] } },
    },
  },
  'normies-polaroid': {
    id: 'normies-polaroid',
    name: 'Normies Polaroid',
    description: 'Instant-photo frame with caption zone',
    component: NormiesPolaroidLayout,
    defaultFontPreset: 6, // Tech / IBM Plex Mono
    categories: ['normies'],
    isPreset: true,
    accent: '#e3e5e4',
    accentGlow: 'rgba(227, 229, 228, 0.25)',
    contrast: { muted: 0.55, subtle: 0.40, footer: 0.45 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-100, 200] } },
    },
  },
  'normies-before-after': {
    id: 'normies-before-after',
    name: 'Before / After',
    description: 'Canvas Story — original vs current side by side',
    component: NormiesBeforeAfterLayout,
    defaultFontPreset: 6,
    categories: ['normies'],
    isPreset: true,
    accent: '#48494b',
    accentGlow: 'rgba(72, 73, 75, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-50, 50] } },
    },
  },
  'normies-dashboard': {
    id: 'normies-dashboard',
    name: 'Dashboard',
    description: 'Market stats & collection data dashboard',
    component: NormiesDashboardLayout,
    defaultFontPreset: 6,
    categories: ['normies'],
    isPreset: true,
    accent: '#48494b',
    accentGlow: 'rgba(72, 73, 75, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-100, 150] } },
    },
  },
  'normies-burn': {
    id: 'normies-burn',
    name: 'Burn Memorial',
    description: 'Dramatic memorial for burned Normies',
    component: NormiesBurnLayout,
    defaultFontPreset: 6,
    categories: ['normies'],
    isPreset: true,
    accent: '#ef4444',
    accentGlow: 'rgba(239, 68, 68, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-50, 100] } },
    },
  },
  'normies-gm': {
    id: 'normies-gm',
    name: 'GM',
    description: 'Bold daily GM post',
    component: NormiesGmLayout,
    defaultFontPreset: 6,
    categories: ['normies'],
    isPreset: true,
    accent: '#fbbf24',
    accentGlow: 'rgba(251, 191, 36, 0.25)',
    contrast: { muted: 0.55, subtle: 0.40, footer: 0.45 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-80, 120] } },
    },
  },
  'normies-explainer': {
    id: 'normies-explainer',
    name: 'Explainer',
    description: 'Educational info card',
    component: NormiesExplainerLayout,
    defaultFontPreset: 6,
    categories: ['normies'],
    isPreset: true,
    accent: '#60a5fa',
    accentGlow: 'rgba(96, 165, 250, 0.25)',
    contrast: { muted: 0.60, subtle: 0.40, footer: 0.50 },
    nudgeConstraints: {
      content: { axis: 'y', range: { y: [-80, 120] } },
    },
  },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);
export const PRESET_TEMPLATES = TEMPLATE_LIST.filter(t => t.isPreset);
export const CUSTOM_TEMPLATES = TEMPLATE_LIST.filter(t => t.isCustom);
export const DEFAULT_TEMPLATE = 'forest-classic';
